import {
    UPDATE_LOAD,
    RESET_LOAD,
    UPDATE_STOP,
    RESET_STOP,
    UPDATE_EVIDENCE,
    RESET_EVIDENCE
} from "./actionTypes";
import * as Yup from "yup";

const loadBase = {
    id: null,
    charges: "",
    client_id: "",
    client_work_order: "",
    practical_miles: "",
    status_id: "",
    schedule_id: "",
    operator_id: "",
    truck_id: "",
    trailer_id: "",
    current_mileage_truck: "",
    current_mileage_trailer: "",
    truck_last_load: null,
    trailer_last_load: null,
    stops: [],
    stops_deleted: [],
    evidences: [],
    evidences_deleted: [],
    payments: [],
}

const stopBase = {
    id: '',
    name: '',
    order: '',
    status: {},
    status_id: '',
    trailer_id: '',
    mile: '',
    lat: 37.7749,
    lng: -122.4194,
    date: '',
    load_time: '',
    delivery_date: '',
    delivery_time: '',
    geofence: 1,
}

const evidenceBase = {
    id: '',
    description: "",
    document: "",
}

const buildDateTime = (dateVal, timeStr) => {
    if (!dateVal || !timeStr) return null;

    // 1. Aseguramos que trabajamos con una copia de la fecha para no modificar la original
    const newDate = new Date(dateVal);

    // 2. Detectar si es PM o AM
    // Convertimos a minúsculas para evitar problemas con "P. M." vs "p. m."
    const timeLower = timeStr.toLowerCase();
    const isPM = timeLower.includes('p. m.') || timeLower.includes('pm');
    const isAM = timeLower.includes('a. m.') || timeLower.includes('am');

    // 3. Limpiar el string para obtener solo los números "06:10"
    // Esto elimina letras y espacios, dejando solo números y los dos puntos
    const cleanTime = timeStr.replace(/[^0-9:]/g, '');

    let [hours, minutes] = cleanTime.split(':').map(Number);

    // 4. Convertir a formato 24 horas para que JavaScript entienda
    if (isPM && hours < 12) hours += 12;
    if (isAM && hours === 12) hours = 0; // Las 12 AM son las 00 horas

    // 5. Asignar la hora a la fecha
    newDate.setHours(hours);
    newDate.setMinutes(minutes);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    return newDate;
};

const initialState = {
    load: { ...loadBase },
    validationStepOne: {
        charges: Yup.number()
            .typeError("Charges must be a number")
            .required("Charges are required")
            .positive("Charges must be a positive number"),
        client_id: Yup.string().required("Client is required"),
        client_work_order: Yup.string()
            .max(100, "Client work order must be 100 characters or less")
            .matches(/^[A-Za-z0-9\-_\s]*$/, "Client work order may only contain letters, numbers, spaces, dashes and underscores"),
        status_id: Yup.string().required("Status is required"),
        schedule_id: Yup.string().required("Load schedule is required"),
        operator_id: Yup.string().required("Operator is required"),
        truck_id: Yup.string().required("Truck is required"),
        trailer_id: Yup.string().nullable(),
        payments: Yup.array().of(
            Yup.object().shape({
                concept: Yup.string().required('Concept payment is required'),
                amount: Yup.number().required('Amount payment is required')
            })
        ),
    },
    validationsLoad: {
        charges: Yup.number()
            .typeError("Charges must be a number")
            .required("Charges are required")
            .positive("Charges must be a positive number"),
        client_id: Yup.string().required("Client is required"),
        client_work_order: Yup.string()
            .max(100, "Client work order must be 100 characters or less")
            .matches(/^[A-Za-z0-9\-_\s]*$/, "Client work order may only contain letters, numbers, spaces, dashes and underscores"),
        practical_miles: Yup.string().required("Practical miles are required"),
        status_id: Yup.string().required("Status is required"),
        schedule_id: Yup.string().required("Load schedule is required"),
        operator_id: Yup.string().required("Operator is required"),
        truck_id: Yup.string().required("Truck is required"),
        trailer_id: Yup.string().nullable(),
        stops: Yup.array()
            .of(
                Yup.object().shape({
                    order: Yup.number().required("Order is required"),
                    name: Yup.string().required("Stop name is required"),
                    status: Yup.object().shape({
                        name: Yup.string().required("Status name is required"),
                    }),
                })
            )
            .required("At least one stop is required"),
        evidences: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required("File name is required"),
            })
        ),
    },
    //Stops
    stop: { ...stopBase },
    validationsStop: {
        name: Yup.string().required('Name is required'),
        status_id: Yup.string().required('Status is required'),


        date: Yup.date()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError("Invalid date format"),


        load_time: Yup.string()
            .nullable(),


        delivery_date: Yup.date()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .typeError("Invalid date format"),


        delivery_time: Yup.string()
            .nullable()
            .test(
                'delivery-after-pickup',
                'Delivery must be after pickup date and time',
                function (value) {
                    // Obtenemos los valores del formulario
                    const { date, load_time, delivery_date } = this.parent;
                    const delivery_time = value; // El valor actual que estamos validando

                    // Si falta algún dato, dejamos que las validaciones .required hagan su trabajo primero
                    if (!date || !load_time || !delivery_date || !delivery_time) {
                        return true;
                    }

                    // Construimos las fechas completas
                    const pickupDateTime = buildDateTime(date, load_time);
                    const deliveryDateTime = buildDateTime(delivery_date, delivery_time);

                    // Validamos que se hayan podido construir
                    if (!pickupDateTime || !deliveryDateTime) return true;

                    // COMPARACIÓN:
                    // Usamos .getTime() para comparar los milisegundos exactos
                    return deliveryDateTime.getTime() > pickupDateTime.getTime();
                }
            ),

        geofence: Yup.number()
    },
    //evidence
    evidence: { ...evidenceBase },
    validationsEvidence: {
        document: Yup.string().required("Document is required"),
        description: Yup.string().required("Description"),
    },
};

const load = (state = initialState, action) => {
    switch (action.type) {
        case UPDATE_LOAD:
            // Actualizar el estado
            return {
                ...state,
                load: action.payload
            };
        case RESET_LOAD:
            return {
                ...state,
                load: { ...loadBase }
            };
        //Stops
        case UPDATE_STOP:
            // Actualizar el estado
            return {
                ...state,
                load: action.payload
            };
        case RESET_STOP:
            console.log(state, 'stio readx')
            return {
                ...state,
                stop: { ...stopBase }
            };
        // evidence
        case UPDATE_EVIDENCE:
            // Actualizar el estado
            return {
                ...state,
                evidence: action.payload
            };
        case RESET_EVIDENCE:
            return {
                ...state,
                evidence: { ...evidenceBase }
            };
        default:
            return state;
    }
};

export default load;
